import { useState } from "react";
import { toast } from "sonner";
import { Check, Loader2 } from "lucide-react";
import { OndaAvatar } from "../OndaAvatar";
import { CountdownBadge } from "../CountdownTimer";
import { pulsesApi } from "../../lib/pulses";
import { formatApiError } from "../../lib/api";

export function PollCard({ pulse, onUpdate }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [justVoted, setJustVoted] = useState(false);

  const options = pulse.payload.options || [];
  const total = pulse.payload.total_votes || 0;
  const myVoteId = pulse.my_vote_option_id;
  const hasVoted = !!myVoteId || justVoted;

  const vote = async (optId) => {
    if (busy || hasVoted) return;
    setBusy(true);
    setError("");
    try {
      const updated = await pulsesApi.vote(pulse.id, optId);
      setJustVoted(true);
      onUpdate && onUpdate(updated);
      toast.success("Voto enviado correctamente");
    } catch (err) {
      const msg = formatApiError(err);
      setError(msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <article
      className="q-glass rounded-3xl p-5 space-y-4"
      data-testid={`pulse-poll-${pulse.id}`}
    >
      <PulseHeader pulse={pulse} kind="Vibe Check" />
      <h3 className="font-display text-xl font-semibold leading-snug">
        {pulse.payload.question}
      </h3>

      <ul className="space-y-2">
        {options.map((o) => {
          const pct = total > 0 ? Math.round((o.votes / total) * 100) : 0;
          const isMy = myVoteId === o.id;
          return (
            <li key={o.id}>
              <button
                type="button"
                disabled={busy || hasVoted}
                onClick={() => vote(o.id)}
                data-testid={`poll-option-${o.id}`}
                className={
                  "relative w-full text-left rounded-2xl border transition-all overflow-hidden " +
                  (hasVoted
                    ? "cursor-default border-white/10 bg-black/30"
                    : "border-white/10 bg-black/30 hover:border-[color:var(--accent)] hover:bg-[color:var(--accent-soft)]")
                }
              >
                {hasVoted && (
                  <div
                    className="absolute inset-y-0 left-0 transition-[width] duration-700 ease-out"
                    style={{
                      width: `${pct}%`,
                      background: isMy ? "var(--accent)" : "rgba(255,255,255,0.08)",
                      opacity: isMy ? 0.85 : 1,
                    }}
                    aria-hidden="true"
                  />
                )}
                <div className="relative flex items-center justify-between gap-3 px-4 py-2.5">
                  <span
                    className={
                      "text-sm font-medium " +
                      (isMy && hasVoted ? "text-[color:var(--accent-contrast)]" : "text-slate-100")
                    }
                  >
                    {o.text}
                  </span>
                  <span
                    className={
                      "text-xs font-mono " +
                      (isMy && hasVoted ? "text-[color:var(--accent-contrast)]" : "text-slate-300")
                    }
                    data-testid={`poll-option-pct-${o.id}`}
                  >
                    {hasVoted ? `${pct}% · ${o.votes}` : `${o.votes} voto${o.votes === 1 ? "" : "s"}`}
                  </span>
                </div>
              </button>
            </li>
          );
        })}
      </ul>

      <div className="flex items-center justify-between text-xs text-slate-400">
        <span data-testid={`poll-total-${pulse.id}`}>
          {total} voto{total === 1 ? "" : "s"} en total
        </span>
        {busy && <Loader2 size={14} className="animate-spin q-accent-text" />}
      </div>

      {error && (
        <div
          className="text-xs text-amber-300 bg-amber-500/10 border border-amber-500/30 rounded-xl px-3 py-2"
          data-testid={`poll-error-${pulse.id}`}
        >
          {error}
        </div>
      )}
      {justVoted && !error && (
        <div
          className="text-xs q-accent-text bg-[color:var(--accent-soft)] border border-[color:var(--accent)]/30 rounded-xl px-3 py-2 flex items-center gap-2"
          data-testid={`poll-success-${pulse.id}`}
        >
          <Check size={14} /> Voto enviado correctamente
        </div>
      )}
    </article>
  );
}

export function PulseHeader({ pulse, kind }) {
  return (
    <div className="flex items-center gap-3">
      <OndaAvatar src={pulse.author.avatar} name={pulse.author.display_name} size={38} />
      <div className="flex-1 min-w-0">
        <div className="text-sm font-semibold text-slate-100 truncate">
          {pulse.author.display_name}
        </div>
        <div className="font-mono text-[11px] text-slate-500 truncate">
          @{pulse.author.ondatag} · {kind}
        </div>
      </div>
      <CountdownBadge expiresAt={pulse.expires_at} />
    </div>
  );
}
